import { Injectable } from '@angular/core';
import type { Content, TableCell } from 'pdfmake/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { HelperService } from './helper.service';
import { AlarmService } from './alarm.service';

/** Column widths for summary and detail rows (landscape A4). */
const SUMMARY_WIDTHS: (string | number)[] = ['18%', '18%', '22%', '22%', '20%'];
const DETAIL_WIDTHS: (string | number)[] = ['25%', '30%', '15%', '30%'];
const HISTORY_WIDTHS: (string | number)[] = ['22%', '18%', '35%', '25%'];

const DETAIL_TABLE_LAYOUT = {
  fillColor: (rowIndex: number) => (rowIndex === 0 ? '#f0f0f0' : null),
  hLineWidth: (i: number, node: { table: { body: unknown[][] } }) =>
    i === 0 || i === node.table.body.length ? 0.5 : 0.25,
  vLineWidth: () => 0.5,
  hLineColor: () => '#cccccc',
  vLineColor: () => '#cccccc',
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

const INNER_TABLE_LAYOUT = {
  hLineWidth: () => 0.25,
  vLineWidth: () => 0.25,
  hLineColor: () => '#dddddd',
  vLineColor: () => '#dddddd',
  paddingLeft: () => 3,
  paddingRight: () => 3,
  paddingTop: () => 2,
  paddingBottom: () => 2,
};

/** Detail timestamps shown in the detailed-view dialog (alert.details). */
const DETAIL_EVENT_KEYS: { key: string; eventName: string }[] = [
  { key: 'userConnectedAt', eventName: 'USER_CONNECTED_AT' },
  { key: 'userDisconnectedAt', eventName: 'USER_DISCONNECTED_AT' },
  { key: 'anonymizationDisabledAt', eventName: 'ANONYMIZATION_DISABLED_AT' },
  { key: 'anonymizationEnabledAt', eventName: 'ANONYMIZATION_ENABLED_AT' },
  { key: 'microphoneOrSpeakerEnabledAt', eventName: 'MICROPHONE_ENABLED_AT' },
  { key: 'microphoneOrSpeakerDisabledAt', eventName: 'MICROPHONE_DISABLED_AT' },
  { key: 'furnitureViewedAt', eventName: 'FURNITURE_VIEWED_AT' },
];

interface HistoryRow {
  timeString: string;
  eventName: string;
  user: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReportAlertPdfDetailService {
  private alarmReasonTranslations = new Map<string, { translation: string }>();
  private alarmCategoryTranslations = new Map<string, { translation: string }>();

  constructor(
    private translate: TranslateService,
    private helper: HelperService,
    private alarmService: AlarmService,
  ) {
    this.alarmService.getAlarmReasonTranslations().subscribe((translations) => {
      this.alarmReasonTranslations = new Map(
        translations.alarmReason.map((x) => [x.key, x])
      );
      this.alarmCategoryTranslations = new Map(
        translations.alarmCategory.map((x) => [x.key, x])
      );
    });
  }

  buildAlertPdfContent(alerts: any[]): Content[] {
    if (!alerts.length) {
      return [];
    }

    const content: Content[] = [
      {
        table: {
          widths: SUMMARY_WIDTHS,
          body: [
            [
              { text: this.t('START_TIME'), style: 'tableHeader' },
              { text: this.t('DEVICE'), style: 'tableHeader' },
              { text: this.t('EVENT'), style: 'tableHeader' },
              { text: this.t('USER_OR_RECIPIENT'), style: 'tableHeader' },
              { text: this.t('END_TIME'), style: 'tableHeader' },
            ],
          ],
        },
        layout: DETAIL_TABLE_LAYOUT,
        margin: [0, 0, 0, 2],
      },
    ];

    alerts.forEach((alert, index) => {
      if (index > 0) {
        content.push({
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 762, y2: 0, lineWidth: 0.5 }],
          margin: [0, 4, 0, 2],
        });
      }
      content.push(this.buildAlertBlock(alert));
    });

    return content;
  }

  private buildAlertBlock(alert: any): Content {
    const expandedStack: Content[] = [
      {
        text: String(alert.taskId ?? ''),
        style: 'alertTaskId',
        margin: [0, 0, 0, 2],
      },
      this.buildLabeledDetailColumnsTable(alert),
    ];

    const historyTable = this.buildHistoryTable(alert);
    if (historyTable) {
      expandedStack.push(historyTable);
    }

    expandedStack.push(this.buildNotesSection(alert));

    return {
      table: {
        widths: SUMMARY_WIDTHS,
        body: [
          this.buildSummaryCells(alert),
          [
            {
              colSpan: 5,
              stack: expandedStack,
              margin: [0, 2, 0, 0],
            },
            {},
            {},
            {},
            {},
          ],
        ],
      },
      layout: DETAIL_TABLE_LAYOUT,
      margin: [0, 0, 0, 2],
    };
  }

  private buildLabeledDetailColumnsTable(alert: any): Content {
    return {
      table: {
        widths: DETAIL_WIDTHS,
        body: [
          [
            { text: this.t('DEVICE'), style: 'detailColumnHeader' },
            { text: this.t('EVENT'), style: 'detailColumnHeader' },
            { text: this.t('SMS'), style: 'detailColumnHeader' },
            { text: this.t('ALARM_REASONS'), style: 'detailColumnHeader' },
          ],
          [
            this.detailCell(this.buildDeviceColumnLines(alert)),
            this.detailCell(this.buildDeliveryColumnLines(alert)),
            this.detailCell(this.buildSmsColumnLines(alert)),
            this.detailCell(this.buildAlarmReasonColumnLines(alert)),
          ],
        ],
      },
      layout: INNER_TABLE_LAYOUT,
    };
  }

  private buildHistoryTable(alert: any): Content | null {
    const rows = this.buildHistoryRows(alert);
    if (!rows.length) {
      return null;
    }

    const body: TableCell[][] = [
      [
        { text: this.t('DATE'), style: 'detailColumnHeader' },
        { text: this.t('TIME_TEXT'), style: 'detailColumnHeader' },
        { text: this.t('EVENT'), style: 'detailColumnHeader' },
        { text: this.t('USER'), style: 'detailColumnHeader' },
      ],
      ...rows.map((row) => [
        { text: this.formatDatePart(row.timeString), style: 'detailCompact' },
        { text: this.formatTimePart(row.timeString), style: 'detailCompact' },
        { text: this.t(row.eventName), style: 'detailCompact' },
        { text: row.user || '-', style: 'detailCompact' },
      ]),
    ];

    return {
      stack: [
        { text: this.t('HISTORY'), style: 'detailSection', margin: [0, 4, 0, 2] },
        {
          table: {
            widths: HISTORY_WIDTHS,
            body,
          },
          layout: INNER_TABLE_LAYOUT,
        },
      ],
    };
  }

  private buildNotesSection(alert: any): Content {
    const blocks: Content[] = [
      { text: this.t('NOTES'), style: 'detailSection', margin: [0, 6, 0, 2] },
    ];

    if (!alert.commentEvents?.length) {
      blocks.push({ text: '-', style: 'detailCompact' });
      return { stack: blocks };
    }

    alert.commentEvents.forEach((comment: any, index: number) => {
      if (index > 0) {
        blocks.push({
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 700, y2: 0, lineWidth: 0.25 }],
          margin: [0, 3, 0, 3],
        });
      }
      blocks.push({
        text: [
          { text: `${comment.UserName ?? ''} (${comment.UserId ?? ''}) · `, bold: true },
          this.formatDate(comment.CommentTime),
        ],
        style: 'detailCompact',
      });
      blocks.push({
        text: this.plainText(comment.Comment),
        style: 'detailNotesBody',
        margin: [0, 1, 0, 0],
      });
    });

    return { stack: blocks };
  }

  private buildHistoryRows(alert: any): HistoryRow[] {
    const rows: HistoryRow[] = [];

    for (const ema of alert.emaEvents ?? []) {
      if (ema.eventType === 'LOG_NEW_NOTE') {
        continue;
      }
      rows.push({
        timeString: ema.time,
        eventName: ema.eventType,
        user: ema.userName ?? '',
      });
    }

    for (const call of alert.callEvents ?? []) {
      rows.push({
        timeString: call.LastCallTime,
        eventName: 'LAST_CALL',
        user: call.UserName ?? '',
      });
    }

    for (const sms of alert.smsEvents ?? []) {
      rows.push({
        timeString: sms.DeliveringTime ?? sms.SendingTime,
        eventName: 'SMS',
        user: sms.UserName ?? '',
      });
    }

    for (const email of alert.emailEvents ?? []) {
      rows.push({
        timeString: email.DeliveringTime,
        eventName: 'EMAIL',
        user: email.UserName ?? '',
      });
    }

    for (const pressTime of alert.additionalPresses ?? []) {
      rows.push({
        timeString: pressTime,
        eventName: 'ADDITIONAL_PRESS',
        user: '',
      });
    }

    if (alert.details) {
      for (const detail of DETAIL_EVENT_KEYS) {
        const timeString = alert.details[detail.key];
        if (timeString) {
          rows.push({
            timeString,
            eventName: detail.eventName,
            user: '',
          });
        }
      }
    }

    return rows.sort(
      (a, b) => new Date(a.timeString).valueOf() - new Date(b.timeString).valueOf()
    );
  }

  private detailCell(lines: string[]): TableCell {
    const filtered = lines.filter((line) => line !== '');
    return {
      text: filtered.length ? filtered.join('\n') : '-',
      style: 'detailCompact',
    };
  }

  private buildSummaryCells(alert: any): TableCell[] {
    return [
      { text: this.formatDate(alert.receiveTime), style: 'tableCell' },
      { text: this.formatDeviceSummary(alert), style: 'tableCell' },
      { text: this.formatEventSummary(alert), style: 'tableCell' },
      { text: this.formatRecipientSummary(alert), style: 'tableCell' },
      { text: this.formatDate(alert.completeTime), style: 'tableCell' },
    ];
  }

  private buildDeviceColumnLines(alert: any): string[] {
    const lines: string[] = [
      `${this.t('SERIAL_NUMBER')}: ${alert.deviceId ?? ''}`,
    ];

    if (alert.environmentId && alert.environmentId !== '00000000') {
      lines.push(
        `${this.t('BASESTATION')}: ${alert.environmentName ?? ''} (${alert.environmentId})`
      );
    }

    lines.push(
      `${this.t('ALARM_ROUTE')}: ${alert.alertRouterName ?? ''} (${alert.alertRouterId ?? ''})`
    );
    lines.push(`${this.t('AVAILABLE_USERS')}: ${this.formatReceivedByText(alert.receivedBy)}`);

    if (alert.visitOnAlertStarted) {
      lines.push(
        `${this.t('RELATED_VISIT')}: ${this.t('DIGITAL_SUPERVISION_VISIT')} - ${this.formatDate(alert.visitOnAlertStarted)}`
      );
    }

    if (alert.pausedDevices?.length > 0) {
      lines.push(`${this.t('PAUSED_ALARMS')}:`);
      alert.pausedDevices.forEach((device: any) => {
        const activation = this.helper.getActivationName(
          device.actGroup,
          device.actNode,
          `${String(device.deviceID ?? '').substring(0, 2)}-${device.deviceClass}`,
          null,
          0
        );
        lines.push(`  ${device.deviceName ?? ''} (${device.deviceID ?? ''}) ${activation}`);
      });
    }

    return lines;
  }

  private buildDeliveryColumnLines(alert: any): string[] {
    const lines: string[] = [];

    if (alert.emaEvents?.length) {
      alert.emaEvents.forEach((ema: any) => {
        if (ema.eventType === 'LOG_NEW_NOTE') {
          return;
        }

        if (ema.eventType === 'LOG_NEW_ALERT' || ema.eventType === 'LOG_ROUTING_CHANGED') {
          lines.push(
            ema.eventType === 'LOG_ROUTING_CHANGED' ? this.t('SYSTEM') : 'Everon Mobile'
          );
        }

        if (ema.userId !== 'NO_USER') {
          let userLine = `${ema.userName ?? ''} (${ema.userId ?? ''})`;
          if (ema.nfcId) {
            userLine += ` ${this.t('WITH_NFC')} ${ema.nfcName ?? ''} (${ema.nfcId})`;
          }
          lines.push(userLine);
        }

        if (ema.eventType === 'LOG_NEW_STATE_COMPLETED' && ema.userId === 'NO_USER') {
          lines.push(
            `${this.t('EMA_COMPLETE_TIMEOUT')} (${this.getResolverForTimeout(alert.emaEvents)})`
          );
        }

        lines.push(
          `${this.t(ema.eventType)} - ${this.formatDate(ema.time)} (${this.formatElapsed(alert.receiveTime, ema.time)})`
        );
      });
    }

    if (alert.callEvents?.length) {
      lines.push(this.t('CALL+SMS'));
      alert.callEvents.forEach((call: any) => {
        if (!(call.CallCount > 0)) {
          return;
        }
        let userLine = call.UserName ?? '';
        if (call.DialNumber) {
          userLine += ` (${call.DialNumber})`;
        }
        lines.push(userLine);
        const callResultKey = `CALL_${String(call.CallResult ?? '').replace(/ /g, '_')}`;
        let resultLine = `${this.t('CALL_COUNT')} ${call.CallCount} · ${this.t(callResultKey)}`;
        if (call.CallResult !== 'CALL FAILURE') {
          resultLine += ` - ${this.formatDate(call.LastCallTime)} (${this.formatElapsed(alert.receiveTime, call.LastCallTime)})`;
        }
        lines.push(resultLine);
      });
    }

    if (alert.emailEvents?.length) {
      lines.push(this.t('EMAIL'));
      alert.emailEvents.forEach((email: any) => {
        let line = `${email.UserName ?? ''} (${email.UserId ?? ''})`;
        if (email.EmailAddress) {
          line += ` · ${email.EmailAddress}`;
        }
        line += ` · ${this.formatDate(email.DeliveringTime)}`;
        lines.push(line);
      });
    }

    if (alert.resolverName === 'STAFF_ERROR') {
      lines.push(`${this.t('DELIVERY_ERROR')}: ${this.t('DELIVERY_ERROR_INFO')}`);
    }

    return lines;
  }

  private buildSmsColumnLines(alert: any): string[] {
    if (!alert.deliveryType?.includes('SMS')) {
      return [];
    }

    const lines: string[] = [];

    if (!alert.smsEvents?.length) {
      lines.push(this.t('SMS_DELIVERY_ERROR'));
      return lines;
    }

    alert.smsEvents.forEach((sms: any) => {
      let line = `${sms.UserName ?? ''} (${sms.UserId ?? ''})`;
      if (sms.PhoneNumber) {
        line += ` · ${sms.PhoneNumber}`;
      }
      line += ` · ${this.t('MESSAGE_SENT')}: ${this.formatDate(sms.SendingTime)}`;
      const delivered = this.formatDate(sms.DeliveringTime);
      if (delivered !== '-') {
        line += ` · ${this.t('DELIVERED')}: ${delivered}`;
      }
      lines.push(line);
    });

    return lines;
  }

  private buildAlarmReasonColumnLines(alert: any): string[] {
    if (!alert.alarmReasons?.length) {
      return [];
    }

    return this.dedupeAlarmReasons(alert.alarmReasons).map(
      (item: any) =>
        `• ${this.translateAlarmCategory(item.alarmReasonCategory)} - ${this.translateAlarmReason(item.alarmReason)}`
    );
  }

  private formatDeviceSummary(alert: any): string {
    const base = alert.deviceName ? alert.deviceName : alert.deviceId;
    const nfcTag = this.getNfcTagForAlert(alert);
    return nfcTag ? `${base} - ${nfcTag}` : String(base ?? '');
  }

  private formatEventSummary(alert: any): string {
    const activation = this.helper.getActivationName(
      alert.activationGroup,
      alert.activationNode,
      alert.deviceType,
      alert.customActivationName,
      alert.connTimeLimit
    );
    const presses = alert.additionalPresses?.length ?? 0;
    if (
      presses > 0 &&
      (alert.deviceType?.startsWith('04') ||
        alert.deviceType?.startsWith('05') ||
        alert.deviceType?.startsWith('31')) &&
      alert.activationNode == '1'
    ) {
      return `${activation} (+${presses})`;
    }
    return String(activation ?? '');
  }

  private formatRecipientSummary(alert: any): string {
    if (alert.cancelled) {
      return this.t('CANCELLED');
    }
    if (this.shouldShowEmaTimeout(alert)) {
      return `${this.t('EMA_COMPLETE_TIMEOUT')} (${this.getResolverForTimeout(alert.emaEvents)})`;
    }
    if (alert.resolverName === 'STAFF_ERROR') {
      return this.t('DELIVERY_ERROR');
    }
    if (alert.resolverName === 'QR_CODE') {
      return this.t('QR_CODE');
    }
    if (alert.resolverName !== 'NO_USER') {
      return String(alert.resolverName ?? '');
    }
    return this.getDeliveryStr(alert.deliveryType);
  }

  private formatReceivedByText(receivedBy: any[] | undefined): string {
    if (!receivedBy?.length || receivedBy[0]?.serverError) {
      return this.t('NO_INFORMATION_AVAILABLE');
    }
    return receivedBy
      .map((user) => `${user.familyName ?? ''}, ${user.firstName ?? ''} (${user.userId ?? ''})`)
      .join('; ');
  }

  private shouldShowEmaTimeout(alert: any): boolean {
    return alert.resolverName === 'NO_USER' && alert.deliveryType === 'EMA';
  }

  private getNfcTagForAlert(alert: any): string | null {
    if (alert.activationGroup === 2 && alert.activationNode === 2 && alert.emaEvents) {
      const logNewAlertEvent = alert.emaEvents.find(
        (ema: any) => ema.eventType === 'LOG_NEW_ALERT'
      );
      if (logNewAlertEvent) {
        return logNewAlertEvent.nfcName || logNewAlertEvent.nfcId || null;
      }
    }
    return null;
  }

  private getResolverForTimeout(emaEvents: any[]): string {
    if (!emaEvents?.length) {
      return '';
    }
    const startedEvents = emaEvents.filter(
      (e) => e.eventType === 'LOG_NEW_STATE_STARTED' && e.userName
    );
    if (!startedEvents.length) {
      return '';
    }
    return startedEvents[startedEvents.length - 1].userName;
  }

  private getDeliveryStr(type: string): string {
    return type ? this.t(type) : '';
  }

  private dedupeAlarmReasons(
    alarmReasons: { alarmReason: string; alarmReasonCategory: string }[]
  ): { alarmReason: string; alarmReasonCategory: string }[] {
    return alarmReasons.filter(
      (obj, index) =>
        alarmReasons.findIndex(
          (item) =>
            item.alarmReason === obj.alarmReason &&
            item.alarmReasonCategory === obj.alarmReasonCategory
        ) === index
    );
  }

  private translateAlarmReason(reasonKey: string): string {
    return this.alarmReasonTranslations.get(reasonKey)?.translation ?? reasonKey ?? '';
  }

  private translateAlarmCategory(categoryKey: string): string {
    return this.alarmCategoryTranslations.get(categoryKey)?.translation ?? categoryKey ?? '';
  }

  private formatElapsed(startTime: string, endTime: string): string {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes >= 1) {
      return this.translate.instant('TIME_START_MINUTES', {
        minutes,
        seconds: remainingSeconds,
      });
    }
    return this.translate.instant('TIME_START_SECONDS', { seconds: remainingSeconds });
  }

  private formatDate(value: string | undefined): string {
    return value ? this.helper.formatDate(value, 'l LTS') : '-';
  }

  private formatDatePart(value: string | undefined): string {
    return value ? this.helper.formatDate(value, 'l') : '-';
  }

  private formatTimePart(value: string | undefined): string {
    return value ? this.helper.formatDate(value, 'LTS') : '-';
  }

  private plainText(value: string | undefined): string {
    let text = String(value ?? '');
    let previous = '';
    while (text !== previous) {
      previous = text;
      text = text.replace(/<[^>]*>/g, '');
    }
    return text;
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }
}
